import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { countries } from '@repo/toolkit/constants/countries'
import { DocumentIcon } from '@repo/ui/icons'
import ScrollableDiv from '@repo/ui/scrollable-div'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { getStaffIdDocumentRules } from '../../../api/staff/get-staff-id-document-rules'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  DocumentStatusEnum,
  idStatusEnumMapping
} from '../../../lib/types/enums/common/document-status.enum'
import { IDTypeEnum, IDTypeEnumMapping } from '../../../lib/types/enums/common/id-type.enum'
import { isStaffEnglish } from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { Staff } from '../../../lib/types/models/staff/staff'
import { StaffInput } from '../../../lib/types/models/staff/staff-input'
import { getSelectedOption } from '../../../lib/utils/functions/misc.functions'
import TextBreakWrapper from '../../elements/break-wrapper'
import { FormControl, FormField, FormItem } from '../../elements/forms/form'
import IconBox from '../../elements/icon-box'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledRadioInput from '../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import DocumentCard from '../staff/document-card'
import { isStaffPaySlipRequired } from '../staff/utils/diplomas-required-documents'

type Props = {
  disabled?: boolean
  staff?: Pick<Staff, 'id' | 'qualifications' | 'position'>
}

const StaffPersonalSpaceAdminstrativeForm = ({ disabled, staff }: Props) => {
  const form = useFormContext<StaffInput>()
  const { t } = useTranslate()
  const isEnglishStaff = isStaffEnglish(staff)

  const diplomas = useWatch({ control: form.control, name: 'diplomas' })
  const idFile = useWatch({ control: form.control, name: 'idFile' })
  const criminalRecordFile = useWatch({ control: form.control, name: 'criminalRecord' })
  const paySlipFile = useWatch({ control: form.control, name: 'paySlip' })
  const etaFile = useWatch({ control: form.control, name: 'eta' })
  const staffApplication = useWatch({ control: form.control, name: 'application' })

  const { data: idDocumentRules } = useQuery({
    queryKey: ['staff-id-document-rules', staff?.id],
    queryFn: () => {
      if (staff?.id) return getStaffIdDocumentRules(staff.id)
    },
    enabled: !!staff?.id
  })

  // The identity card cannot be chosen while a stay of the staff requires a passport
  const isIdTypeDisabled = (idType: IDTypeEnum) =>
    !!idDocumentRules?.passportRequired && idType === IDTypeEnum.IDENTITY_CARD

  // An empty identity document starts on the identity card, move it to the passport when required
  const idType = form.watch('idType')
  const idNumber = form.watch('idNumber')
  useEffect(() => {
    if (
      !disabled &&
      idDocumentRules?.passportRequired &&
      idType === IDTypeEnum.IDENTITY_CARD &&
      !idNumber &&
      !idFile
    )
      form.setValue('idType', IDTypeEnum.PASSPORT, { shouldDirty: true })
  }, [form, disabled, idDocumentRules?.passportRequired, idType, idNumber, idFile])

  const hasPaySlip = isStaffPaySlipRequired({
    position: staff?.position,
    qualifications: staff?.qualifications,
    diplomas,
    memberOfNationalEducationStatus: staffApplication?.memberOfNationalEducationStatus
  })

  return (
    <IconBox
      icon={DocumentIcon}
      title={t('staff.personal.space.adminstrative.files')}
      titleColor={tailwindColors.black.DEFAULT}
      titleVariant="h6"
    >
      <div className="h-4" id="adminstrative-documents" />
      <ScrollableDiv className="flex w-full gap-5 mobile:flex-col ">
        <DocumentCard
          id="id-card"
          disabled={disabled}
          className={cn({ 'w-80 flex-shrink-0 mobile:w-full': hasPaySlip })}
          title={t('staff.personal.space.identity.document')}
          mapping={idStatusEnumMapping}
          informationTooltip={{
            tooltipInformation: (
              <div className="flex flex-col gap-3">
                <TextBreakWrapper
                  translationKey="staff.personal.space.identity.card.first.info"
                  variant="label-medium"
                  color={tailwindColors.white.DEFAULT}
                />
                <TextBreakWrapper
                  translationKey="staff.personal.space.identity.card.second.info"
                  variant="label-medium"
                  color={tailwindColors.white.DEFAULT}
                />
                <TextBreakWrapper
                  translationKey="staff.personal.space.identity.card.third.info"
                  variant="label-medium"
                  color={tailwindColors.white.DEFAULT}
                />
              </div>
            ),
            subInformation: t('staff.personal.space.identity.subtitle')
          }}
          status={form.watch('idStatus')}
          statusPosition="inside"
          files={idFile ? [idFile] : []}
          onFilesChange={(files) => {
            form.setValue('idFile', files[0])
            if (files[0]) {
              form.setValue('idStatus', DocumentStatusEnum.RECEIVED)
            } else {
              form.setValue('idStatus', DocumentStatusEnum.PENDING)
            }
          }}
        >
          <div className={cn('flex flex-col gap-3', { 'pointer-events-none': disabled })}>
            <Typography variant={'tag'} color={tailwindColors.grey.DEFAULT} className="mt-5">
              Type:
            </Typography>
            <div className="grid grid-cols-2 items-center gap-3 w-full ">
              {Object.values(IDTypeEnum).map((id) => (
                <div className="flex gap-2 " key={id}>
                  <FormField
                    key={id}
                    control={form.control}
                    name="idType"
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <FormControl>
                            <ControlledRadioInput
                              {...field}
                              disabled={field.disabled || isIdTypeDisabled(id)}
                              label={t(IDTypeEnumMapping[id].title)}
                              checked={field.value === id}
                              onCheck={() => field.onChange(id)}
                            />
                          </FormControl>
                        </FormItem>
                      )
                    }}
                  />
                  {IDTypeEnumMapping[id].information}
                </div>
              ))}
            </div>
            <FormField
              control={form.control}
              name="idNumber"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput
                      labelClassName={cn({ 'bg-white-hover': disabled })}
                      {...field}
                      label="N°"
                      type="text"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="idDeliveredAt"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledDateStringInput
                      {...field}
                      labelClassName={cn({ 'bg-white-hover': disabled })}
                      label={t('staff.personal.space.date.of.issue')}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="idValidUntil"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledDateStringInput
                      {...field}
                      labelClassName={cn({ 'bg-white-hover': disabled })}
                      label={t('staff.personal.space.expiration.date')}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="idDeliveredBy"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      labelClassName={cn({ 'bg-white-hover': disabled })}
                      inputClassName={cn({ 'bg-white-hover': disabled })}
                      options={countries.map((country) => ({ label: country, value: country }))}
                      label={t('staff.personal.space.issuing.country')}
                      searchable
                      selectedOption={getSelectedOption(field.value, field.value)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="h-3" />
          </div>
        </DocumentCard>
        {hasPaySlip && (
          <DocumentCard
            id="pay-slip"
            className={cn({
              'w-80 flex-shrink-0 mobile:w-full': hasPaySlip
            })}
            disabled={form.formState.defaultValues?.paySlipStatus === DocumentStatusEnum.VERIFIED}
            title={t('staff.personal.space.payslip')}
            errorMessage={form.formState.errors.paySlip?.message}
            statusPosition="inside"
            informationTooltip={{
              tooltipInformation: (
                <TextBreakWrapper
                  variant="label-medium"
                  color={tailwindColors.white.DEFAULT}
                  translationKey={'staff.personal.space.payslip.subtitle'}
                />
              )
            }}
            status={form.watch('paySlipStatus')}
            onStatusChange={(status) => {
              form.setValue('paySlipStatus', status)
            }}
            files={paySlipFile ? [paySlipFile] : []}
            onFilesChange={(files) => {
              form.setValue('paySlip', files[0], { shouldValidate: true })
              if (files[0]) form.setValue('paySlipStatus', DocumentStatusEnum.RECEIVED)
              else form.setValue('paySlipStatus', DocumentStatusEnum.PENDING)
            }}
          />
        )}
        <DocumentCard
          id="criminal-record"
          className={cn({ 'w-80 flex-shrink-0 mobile:w-full': hasPaySlip })}
          disabled={
            form.formState.defaultValues?.criminalRecordStatus === DocumentStatusEnum.VERIFIED
          }
          informationTooltip={{
            tooltipInformation: (
              <div className="gap-2 flex flex-col">
                <TextBreakWrapper
                  translationKey="staff.personal.space.DBS.first.info"
                  variant="label-medium"
                  color={tailwindColors.white.DEFAULT}
                />
                <TextBreakWrapper
                  translationKey="staff.personal.space.DBS.second.info"
                  variant="label-medium"
                  color={tailwindColors.white.DEFAULT}
                >
                  <a
                    href="https://casier-judiciaire.justice.gouv.fr/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-blue"
                  >
                    https://casier-judiciaire.justice.gouv.fr
                  </a>
                </TextBreakWrapper>
              </div>
            ),
            subInformation: t('staff.personal.space.DBS.subtitle')
          }}
          title={t('staff.personal.space.DBS')}
          statusPosition="inside"
          status={form.watch('criminalRecordStatus')}
          onStatusChange={(status) => {
            form.setValue('criminalRecordStatus', status)
          }}
          files={criminalRecordFile ? [criminalRecordFile] : []}
          onFilesChange={(files) => {
            form.setValue('criminalRecord', files[0])
            if (files[0]) form.setValue('criminalRecordStatus', DocumentStatusEnum.RECEIVED)
            else form.setValue('criminalRecordStatus', DocumentStatusEnum.PENDING)
          }}
        />
        {!isEnglishStaff && (
          <DocumentCard
            id="eta-card"
            className={cn({ 'w-80 flex-shrink-0 mobile:w-full': hasPaySlip })}
            disabled={form.formState.defaultValues?.etaStatus === DocumentStatusEnum.VERIFIED}
            title="ETA/ESTA/AVE"
            statusPosition="inside"
            informationTooltip={{
              subInformation: t('staff.personal.space.eta.subtitle')
            }}
            status={form.watch('etaStatus')}
            onStatusChange={(status) => {
              form.setValue('etaStatus', status)
            }}
            files={etaFile ? [etaFile] : []}
            onFilesChange={(files) => {
              form.setValue('eta', files[0])
              if (files[0]) form.setValue('etaStatus', DocumentStatusEnum.RECEIVED)
              else form.setValue('etaStatus', DocumentStatusEnum.PENDING)
            }}
          />
        )}
      </ScrollableDiv>
    </IconBox>
  )
}

export default StaffPersonalSpaceAdminstrativeForm
